<template>
  <div class="login-wrapper apcn">
    <div class="login-box">
      <div class="row cam-wrap" v-show="!showRetry">
        <div class="col body m-0">
          <h4>{{ $t('login.title') }}</h4>
          <div class="cam-inner">
            <div class="item limited" ref="face_fail">
              <div class="inner">
                <i class="icon icon-issue_1"></i>
                <p>
                  <strong v-if="examStatus.state === 'exam_end'"
                    >{{ $t('userStateMessage.examEnd') }}.</strong
                  >
                  <strong v-else-if="failStatus">
                    <i18n path="error.adminNotFound">
                      <template #br> <br /> </template>
                    </i18n>
                  </strong>
                  <strong v-else>{{ $t('login.limit.row1') }}</strong>
                </p>
                <i18n tag="p" path="login.limit.row2">
                  <template #inquiry>
                    <a :href="inquiryLink" :target="inquiryTarget">{{
                      examInfo.contactLink
                    }}</a>
                  </template>
                  <template #br>
                    <br />
                  </template>
                </i18n>
              </div>
            </div>
            <div class="item cam-box">
              <div
                class="loading"
                id="regist_face_loading"
                style="display: none"></div>
              <div class="video-box">
                <div class="item guide-line">
                  <img
                    src="@/assets/images/login/face_outline_640.png"
                    alt="얼굴위치 가이드라인" />
                </div>
                <canvas
                  ref="canvasOutput"
                  class="item output"
                  :style="
                    drawRectangle
                      ? {
                          width: '100%',
                          height: '100%',
                          zIndex: 1000,
                        }
                      : {display: 'none'}
                  " />

                <video
                  autoplay
                  playsinline
                  ref="face_video"
                  @play="onPlayVideo"
                  muted>
                  {{ $t('login.notSupportVideoTag') }}
                </video>
              </div>
              <i18n tag="p" class="text-guide" :path="guideMessage">
                <template #br>
                  <br />
                </template>
              </i18n>
            </div>
          </div>
        </div>
      </div>
      <RetryLoginGuide
        v-show="showRetry"
        :userId="userId"
        :companyId="this.examInfo.companyId"
        :contractLink="inquiryLink"
        :contractLinkText="examInfo.contactLink"
        :contractTarget="inquiryTarget"
        :loginFailAction="examInfo.loginFailAction"
        @retryLogin="retryLogin"></RetryLoginGuide>
    </div>
  </div>
</template>

<script>
  import Mediapipe from '@/js/video/media_pipe';
  import {
    getInquiryLink,
    getInquiryTarget,
    changeLocale,
  } from '@/js/common/util';
  import {
    checkPermissionDevice,
    getStream,
    captureImage,
    stopLocalCam,
  } from '@/js/utils/camera';
  import {
    CURRENT_USER_STATE,
    CUSTOM_COMPANIES,
    VIDEO_SIZE,
    EXAM_RENDER_TYPE,
    otmUserServerUrl,
    USER_V3_STATE_TYPE,
  } from '@/js/common/constants';
  import initDialogs from '@/components/modal/loginModalTemplates';
  import examRoom from '@/mixins/exam_room';
  import info from '@/mixins/info';
  import RetryLoginGuide from '../components/RetryLoginGuide.vue';
  import api from '@/api';
  import SentryHelper from '@/js/common/sentry';

  export default {
    name: 'Login',
    metaInfo() {
      return {title: this.examInfo.name};
    },
    components: {RetryLoginGuide},
    mixins: [examRoom, info],
    data() {
      return {
        mediapipe: null,
        userId: null,
        examInfo: {},
        periodInfo: {},
        examStatus: {},
        userInfo: {
          status: null,
        },
        failStatus: false,

        mediapipeLoaded: false,
        verifyFailCnt: 0,
        exposeGuideLineMessage: false,
        showRetry: false,
      };
    },
    computed: {
      inquiryLink() {
        return getInquiryLink(this.examInfo);
      },
      inquiryTarget() {
        return getInquiryTarget(this.examInfo);
      },
      drawRectangle() {
        return this.mediapipeLoaded;
      },
      guideMessage() {
        return this.exposeGuideLineMessage ? 'login.line' : 'login.guide';
      },
    },
    async mounted() {
      initDialogs.call(this, this.moveExamRoom);
      const {userid} = this.$route.query;
      if (!userid) {
        this.showModal(null, this.$t('error.requiredParamNotFound'));
        this.hideModal(null);
        return;
      }

      const {userId, videoRoomId, state, contactLink} = await this.init(
        Number(userid),
      );

      if (!videoRoomId || state === CURRENT_USER_STATE.EXAM_END) {
        this.failStatus = true;
        this.$refs['face_fail'].classList.add('active');
        return;
      }

      const check = await this.checkVideoPermission(userId, contactLink);
      if (!check) return;

      this.loadMediapipe();
    },
    methods: {
      async init(userid) {
        this.userId = userid;
        const {user, exam, period} = await this.fetchData(userid);
        this.userInfo = user;
        this.examInfo = exam;
        this.periodInfo = period;

        if (exam) {
          this.favicon(exam.icon);
        }

        this.examStatus = this.getStatus(
          this.examInfo,
          this.periodInfo,
          this.userInfo,
        );

        changeLocale(this.examInfo.companyId);

        return {
          userId: this.userId,
          videoRoomId: this.userInfo.videoRoomId,
          state: this.examStatus.state,
          contactLink: this.examInfo.contactLink,
        };
      },

      async checkVideoPermission(userId, contactLink) {
        const check = await checkPermissionDevice(false, userId, contactLink);
        return check;
      },

      loadMediapipe() {
        this.mediapipe = new Mediapipe({
          page: 'login',
          drawUse: true,
          videoRef: this.$refs['face_video'],
          canvasRef: this.$refs['canvasOutput'],
          width: VIDEO_SIZE.WIDTH,
          height: VIDEO_SIZE.HEIGHT,
          userId: this.userId,
          frBypass: this.examInfo.loginFailAction === 'pass',
          contrackLink: this.examInfo.contactLink,
          detectTab: false,
          loginPass:
            this.examInfo.loginFailAction === 'pass' &&
            this.examInfo.companyId !== CUSTOM_COMPANIES.HANJA,
          audio: false,
          successModelLoad: () => {
            if (!this.mediapipeLoaded) {
              this.mediapipeLoaded = true;
            }
            this.mediapipe.startCamera();
          },
          failModelLoad: () => {
            this.mediapipeLoaded = false;
            this.openLocalCam();
          },
          sendEvent: result => {
            this.mediapipe.verifyFace(result);
          },
          // 얼굴 확인 성공, 실패
          verifyComplete: () => {
            this.successVerify();
          },
          postFailVerify: () => {
            this.verifyFail();
          },
          // 다음 과정 호출
          passToNextStep: () => {
            this.passToNextStep();
          },
          // 얼굴에 올바른 위치가 되어있지 않았을때 메세지 변경
          guideLineMessage: status => {
            this.exposeGuideLineMessage = status;
          },
        });
      },

      // video가 play되었을때
      onPlayVideo() {
        this.mediapipeLoaded
          ? this.mediapipe.changeDetectStatus(true)
          : this.delaySendLocalCamImage();
      },

      // Local Cam으로 열였을 경우 비디오 종료
      closeLocalCam() {
        const video = this.$refs['face_video'];
        stopLocalCam(video);
      },

      async openLocalCam() {
        const {stream} = await getStream(
          this.userId,
          null,
          false,
          this.examInfo.contactLink,
        );
        const video = this.$refs['face_video'];
        video.srcObject = stream;
      },

      delaySendLocalCamImage(delay = 3000) {
        setTimeout(() => {
          this.sendLocalCamImage();
        }, delay);
      },

      sendLocalCamImage() {
        const video = this.$refs['face_video'];
        const canvas = this.$refs['canvasOutput'];

        const image = captureImage(video, canvas);

        this.verifyFace(image);
      },

      async verifyFace(image) {
        const param = {
          userId: this.userId,
          faceCoords: [],
          image: image,
        };
        try {
          await this.mediapipe.patchVerifyFace(param);
          this.closeLocalCam();
          this.successVerify();
        } catch (error) {
          this.verifyFail();
        }
      },

      async passToNextStep() {
        const video = this.$refs['face_video'];
        const canvas = this.$refs['canvasOutput'];
        const image = captureImage(video, canvas);

        const param = {
          userId: this.userId,
          faceCoords: [],
          image: image,
        };
        try {
          await this.mediapipe.patchVerifyFace(param);
        } catch (error) {
          // patch verify api Error
        } finally {
          this.mediapipeLoaded
            ? this.mediapipe.stopVideo()
            : this.closeLocalCam();
          this.moveExamRoom();
        }
      },
      // 얼굴 인식 실패했을 경우
      faceFail() {
        if (this.examInfo.renderType === EXAM_RENDER_TYPE.EXAM_WORLD_IFRAME) {
          this.iframeFail();
          return;
        }

        if (this.failUrl) {
          location.replace(this.failUrl);
          return;
        }

        this.showRetry = true;
        this.failStatus = false;
      },

      iframeFail() {
        const searchParams = new URLSearchParams(window.location.search);
        const failUrl = searchParams.get('fail_url');
        failUrl && searchParams.set('return_url', failUrl);

        location.href = `${otmUserServerUrl}/${
          USER_V3_STATE_TYPE.EXAM_ROOM_IFRAME
        }?${searchParams.toString()}&companyid=${this.userInfo.companyId}`;
      },

      // 얼굴 확인 성공시
      successVerify() {
        this.toggleModal({
          type: 'success',
          modal: this.modalInit,
          flag: true,
        });
      },

      // 얼굴 확인 실패시
      verifyFail() {
        if (this.verifyFailCnt > 2) {
          this.verifyFailCnt = 0;
          this.faceFail();

          this.mediapipeLoaded
            ? this.mediapipe.stopVideo()
            : this.closeLocalCam();
          return;
        }

        this.verifyFailCnt++;
        this.loginNotMatch(this.verifyFailCnt, () => {
          this.mediapipeLoaded
            ? this.mediapipe.changeDetectStatus(true)
            : this.delaySendLocalCamImage();
        });
      },

      // 얼굴이 일치하지 않을 경우 (3번)
      loginNotMatch(verifyFailCnt, callback) {
        const inquiryCompanyIds = [CUSTOM_COMPANIES.KUMON];
        if (this.examInfo.loginFailAction === 'pass' && verifyFailCnt === 3) {
          this.mediapipe.stopVideo();

          const byPassText = inquiryCompanyIds.includes(this.examInfo.companyId)
            ? 'login.byPass.inquiry'
            : this.examInfo.companyId === CUSTOM_COMPANIES.KT
            ? 'login.byPass.kt'
            : 'login.byPass.default';

          this.showModal(
            null,
            this.$t(byPassText, {
              inquiry: this.examInfo.contactLink,
            }),
          );
          this.hideModal(null, () => this.failByPass());
          return;
        }

        this.toggleModal({
          type: 'fail',
          modal: this.loginFailModalInit,
          count: `(${verifyFailCnt}/3)`,
          flag: true,
        });

        if (verifyFailCnt === 3) {
          this.mediapipe.stopVideo();
          this.loginCloseModal({
            modal: this.loginFailModalInit,
            callback: this.faceFail,
            flag: false,
          });
          return;
        }

        this.loginCloseModal({
          modal: this.loginFailModalInit,
          callback: () => {
            callback();
            if (this.mediapipeLoaded) {
              this.mediapipe.startRAF();
            }
          },
          flag: false,
        });
      },

      async failByPass() {
        try {
          await api.loginFailByPass(this.userId);
        } catch (error) {
          SentryHelper.captureMessage({
            error,
            userId: this.userId,
            messageTitle: `[Login Bypass API] callback error: ${error}`,
          });
        } finally {
          this.moveExamRoom();
        }
      },

      async retryLogin() {
        const check = await this.checkVideoPermission(
          this.userId,
          this.examInfo.contactLink,
        );

        if (check) {
          this.showRetry = false;
          this.verifyFailCnt = 0;
          this.mediapipe ? this.mediapipe.startCamera() : this.loadMediapipe();
        }
      },

      toggleModal(modalInfo) {
        if (modalInfo.flag) {
          this.mediapipe.cancelRAF();
        }
        let modalId = null;
        let message = null;
        if (modalInfo.type === 'fail') {
          modalId = '.modal-face-fail-text';
          message = this.$t('login.faceNotMatch');
          modalInfo.modal.querySelector('.strong-text.bull-none').innerText =
            modalInfo.count;
        } else {
          modalId = '.modal-face-success-text';
          message = this.$t('login.faceMatch');
        }

        modalInfo.modal.querySelector(modalId).innerText = message;
        modalInfo.modal.style.display = modalInfo.flag ? 'block' : 'none';
      },

      loginCloseModal(modalInfo) {
        modalInfo.modal
          .querySelector('#fail-ok')
          .addEventListener('click', () => {
            modalInfo.modal.style.display = modalInfo.flag ? 'block' : 'none';
            if (typeof modalInfo.callback === 'function') {
              modalInfo.callback();
              modalInfo.callback = null;
            }
          });
      },
    },
  };
</script>

<style src="@/assets/scss/login.scss" lang="scss" scoped></style>
